import React, { useMemo } from "react";
import { Paper, Box, Typography, Tooltip } from "@material-ui/core";
import { withStyles, Theme } from "@material-ui/core/styles";
import intl from "react-intl-universal";
import TooltipIcon from "@material-ui/icons/HelpOutline";

import { IWithballData } from "types";
import {
  groupWithBallDataByActionType,
  groupWithBallDataByPlayer,
  calculateActiveEventDistance,
  getEffectiveEvent,
} from "utils";
import { EVENT_VALUES } from "modules/withball-data-input/constants";
import { IActiveValueData } from "utils";

import { TouchBallPanel } from "./TouchBallPannel";
import { PassPanel } from "./PassPanel";
import { DribblePanel } from "./DribblePannel";
import { ShootPannel } from "./ShootPannel";
import { DefensePanel } from "./DefensePanel";
import { PossessionPanel } from "./PossessionPanel";
import { DisciplinePanel } from "./DisciplinePanel";
import { ActiveValuePanel } from "./ActiveValuePanel";

interface ITeamDataOverviewProps {
  dataArray: IWithballData[];
}

const HtmlTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 1200,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}))(Tooltip);

export const TeamDataOverview = ({ dataArray }: ITeamDataOverviewProps) => {
  const groupedStats = useMemo(() => {
    return groupWithBallDataByActionType(dataArray);
  }, [dataArray]);
  const effectiveTouchBallDistances = useMemo(() => {
    const playerGroupedData = groupWithBallDataByPlayer(dataArray, true);

    const efficiencyValue: IActiveValueData = Object.keys(
      playerGroupedData
    ).reduce((accumulator: IActiveValueData, key) => {
      const each = playerGroupedData[key];
      const totalDistance = calculateActiveEventDistance(
        parseInt(key),
        each.data,
        true
      );
      const effectiveDistance = calculateActiveEventDistance(
        parseInt(key),
        each.data,
        false
      );
      accumulator[key] = {
        totalDistance,
        effectiveDistance,
        name: each.name,
        events: each.data.length,
        effectiveEvents: getEffectiveEvent(parseInt(key), each.data).length,
      };
      return accumulator;
    }, {});

    return efficiencyValue;
  }, [dataArray]);
  return (
    <>
      <Paper>
        <Box padding={2}>
          <Typography variant="h3">
            {intl.get("STATS_LABEL_TOUCH_BALL")}
          </Typography>
          <Box mt={2} />
          <TouchBallPanel dataArray={dataArray || []} />
        </Box>
      </Paper>
      <Box mt={2} />
      <Paper>
        <Box padding={2}>
          <Typography variant="h3">
            {intl.get("STATS_LABEL_ACTIVE_VALUE")}{" "}
            <HtmlTooltip
              title={
                <>
                  <Typography color="inherit">{`${intl.get(
                    "STATS_LABEL_ACTIVE_EVENT_AMOUNT"
                  )}: ${intl.get(
                    "STATS_LABEL_ACTIVE_EVENT_AMOUNT_DESC"
                  )}`}</Typography>
                  <Typography color="inherit">{`${intl.get(
                    "STATS_LABEL_EFFECTIVE_ACTIVE_EVENT_AMOUNT"
                  )}: ${intl.get(
                    "STATS_LABEL_EFFECTIVE_ACTIVE_EVENT_AMOUNT_DESC"
                  )}`}</Typography>
                  <Typography color="inherit">{`${intl.get(
                    "STATS_LABEL_ACTIVE_EVENT_DISTANCE"
                  )}: ${intl.get(
                    "STATS_LABEL_ACTIVE_EVENT_DISTANCE_DESC"
                  )}`}</Typography>
                  <Typography color="inherit">{`${intl.get(
                    "STATS_LABEL_EFFECTIVE_EVENT_DISTANCE"
                  )}: ${intl.get(
                    "STATS_LABEL_EFFECTIVE_EVENT_DISTANCE_DESC"
                  )}`}</Typography>
                </>
              }
            >
              <TooltipIcon fontSize="small" />
            </HtmlTooltip>
          </Typography>

          <Box mt={2} />
          <ActiveValuePanel data={effectiveTouchBallDistances} />
        </Box>
      </Paper>
      <Box mt={2} />
      <Paper>
        <Box padding={2}>
          <Typography variant="h3">{intl.get("STATS_LABEL_PASS")}</Typography>
          <Box mt={2} />
          <PassPanel dataArray={groupedStats[EVENT_VALUES.PASS] || []} />
        </Box>
      </Paper>
      <Box mt={2} />
      <Paper>
        <Box padding={2}>
          <Typography variant="h3">
            {intl.get("STATS_LABEL_DRIBBLE")}
          </Typography>
          <Box mt={2} />
          <DribblePanel dataArray={groupedStats[EVENT_VALUES.DRIBBLE] || []} />
        </Box>
      </Paper>
      <Box mt={2} />
      <Paper>
        <Box padding={2}>
          <Typography variant="h3">{intl.get("STATS_LABEL_SHOOT")}</Typography>
          <Box mt={2} />
          <ShootPannel dataArray={groupedStats[EVENT_VALUES.SHOOT] || []} />
        </Box>
      </Paper>
      <Box mt={2} />
      <Paper>
        <Box padding={2}>
          <Typography variant="h3">
            {intl.get("STATS_LABEL_DEFENCE")}
          </Typography>
          <Box mt={2} />
          <DefensePanel
            tackleDataArray={groupedStats[EVENT_VALUES.TACKLE] || []}
            interceptionDataArray={
              groupedStats[EVENT_VALUES.INTERCEPTION] || []
            }
            clearanceDataArray={groupedStats[EVENT_VALUES.CLEARANCE] || []}
          />
        </Box>
      </Paper>
      <Box mt={2} />
      <Paper>
        <Box padding={2}>
          <Typography variant="h3">{intl.get("STATS_POSSESSION")}</Typography>
          <Box mt={2} />
          <PossessionPanel dataArray={dataArray} />
        </Box>
      </Paper>
      <Box mt={2} />
      <Paper>
        <Box padding={2}>
          <Typography variant="h3">
            {intl.get("STATS_LABEL_DISCIPLINE")}
          </Typography>
          <Box mt={2} />
          <DisciplinePanel
            foulDataArray={groupedStats[EVENT_VALUES.FOUL] || []}
            yellowCardDataArray={groupedStats[EVENT_VALUES.YELLOW] || []}
            redCardDataArray={groupedStats[EVENT_VALUES.RED] || []}
          />
        </Box>
      </Paper>
    </>
  );
};
